import type { CSSProperties, ReactNode } from 'react';
import { Sparkline } from './Sparkline';
import { cn } from '../../lib/utils';

export interface KPICardProps {
  label: string;
  value: ReactNode;
  sub?: string;
  trend?: string;
  color?: string;
  sparkline?: number[];
  className?: string;
}

function isHexColor(value: string): boolean {
  return /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/.test(value);
}

export function KPICard({
  label,
  value,
  sub,
  trend,
  color,
  sparkline,
  className,
}: KPICardProps) {
  const trendStyle: CSSProperties | undefined =
    color && isHexColor(color) ? { color } : undefined;
  const trendColorClass = color && !isHexColor(color) ? color : undefined;
  return (
    <div className={cn('relative bg-surface px-[14px] py-3', className)}>
      <div className="font-mono text-[10.5px] font-bold tracking-[.06em] uppercase text-textMute">
        {label}
      </div>
      <div className="flex items-baseline gap-1.5 mt-1.5">
        <span className="text-[22px] sm:text-[28px] font-bold text-text tabular-nums leading-none tracking-[-.02em]">
          {value}
        </span>
        {sub && (
          <span className="text-[10.5px] font-semibold text-textSoft">
            {sub}
          </span>
        )}
      </div>
      <div className="flex items-center gap-1.5 mt-1.5 min-h-[18px]">
        {trend && (
          <span
            style={trendStyle}
            className={cn('font-mono text-[10.5px] font-bold', trendColorClass)}
          >
            {trend}
          </span>
        )}
        {sparkline && sparkline.length > 0 && (
          <div className="flex-1 opacity-70">
            <Sparkline
              data={sparkline}
              w="100%"
              h={28}
              color={color ?? 'currentColor'}
              strokeWidth={1.5}
            />
          </div>
        )}
      </div>
    </div>
  );
}

KPICard.displayName = 'KPICard';

export default KPICard;
